/**
 * Skills System - Like Claude Code /commands
 * Implements common development workflows
 */

export const SKILLS = {
  '/gsd:new-project': {
    name: 'New Project',
    description: 'Initialize a new project with roadmap',
    agent: 'gsd-roadmapper',
    workflow: async (agentManager, projectDescription) => {
      // 1. Research domain
      const researchAgent = await agentManager.spawnAgent('GSD_ROADMAPPER',
        `Research domain and create roadmap for: ${projectDescription}`
      );

      const roadmap = await agentManager.runAgent(researchAgent, projectDescription);

      return {
        roadmap,
        nextStep: 'Use /gsd:plan-phase to plan first phase'
      };
    }
  },

  '/gsd:plan-phase': {
    name: 'Plan Phase',
    description: 'Create detailed plan for a phase',
    agent: 'gsd-planner',
    workflow: async (agentManager, phaseDescription) => {
      const plannerAgent = await agentManager.spawnAgent('GSD_PLANNER',
        `Create detailed execution plan for: ${phaseDescription}`
      );

      const plan = await agentManager.runAgent(plannerAgent, phaseDescription, {
        enableTools: true
      });

      return {
        plan,
        nextStep: 'Use /gsd:execute to execute the plan'
      };
    }
  },

  '/gsd:execute': {
    name: 'Execute Plan',
    description: 'Execute a planned phase',
    agent: 'gsd-executor',
    workflow: async (agentManager, plan) => {
      const executorAgent = await agentManager.spawnAgent('GSD_EXECUTOR',
        'Execute the planned tasks with atomic commits'
      );

      const result = await agentManager.runAgent(executorAgent,
        `Execute this plan:\n${JSON.stringify(plan, null, 2)}`,
        { enableTools: true }
      );

      return {
        result,
        nextStep: 'Use /gsd:verify to verify completion'
      };
    }
  },

  '/gsd:verify': {
    name: 'Verify Work',
    description: 'Verify phase goals achieved',
    agent: 'gsd-verifier',
    workflow: async (agentManager, goal) => {
      const verifierAgent = await agentManager.spawnAgent('GSD_VERIFIER',
        'Verify goal achievement through goal-backward analysis'
      );

      const verification = await agentManager.runAgent(verifierAgent,
        `Verify this goal was achieved: ${goal}`,
        { enableTools: true }
      );

      return {
        verification,
        passed: verification.content.includes('PASS') || verification.content.includes('✓')
      };
    }
  },

  '/gsd:debug': {
    name: 'Debug Issue',
    description: 'Investigate and fix bugs',
    agent: 'gsd-debugger',
    workflow: async (agentManager, issueDescription) => {
      const debuggerAgent = await agentManager.spawnAgent('GSD_DEBUGGER',
        'Investigate bug using scientific method'
      );

      const investigation = await agentManager.runAgent(debuggerAgent,
        `Debug this issue: ${issueDescription}`,
        { enableTools: true }
      );

      return {
        investigation,
        fix: investigation.toolCalls.find(t => t.tool.includes('write_file'))
      };
    }
  },

  '/review': {
    name: 'Code Review',
    description: 'Review code for quality and security',
    agent: 'devteam-reviewer',
    workflow: async (agentManager, codeOrPath) => {
      const reviewerAgent = await agentManager.spawnAgent('DEVTEAM_REVIEWER',
        'Review code for correctness, security, performance'
      );

      const review = await agentManager.runAgent(reviewerAgent,
        `Review this code:\n${codeOrPath}`,
        { enableTools: true }
      );

      return {
        review,
        issues: review.toolCalls.length
      };
    }
  },

  '/test': {
    name: 'Generate Tests',
    description: 'Create comprehensive tests',
    agent: 'devteam-qa',
    workflow: async (agentManager, codeOrPath) => {
      const qaAgent = await agentManager.spawnAgent('DEVTEAM_QA',
        'Create comprehensive tests with high coverage'
      );

      const tests = await agentManager.runAgent(qaAgent,
        `Generate tests for:\n${codeOrPath}`,
        { enableTools: true }
      );

      return {
        tests,
        files: tests.toolCalls.filter(t => t.tool.includes('write_file')).length
      };
    }
  },

  '/docs': {
    name: 'Generate Documentation',
    description: 'Create documentation',
    agent: 'devteam-docs',
    workflow: async (agentManager, scope) => {
      const docsAgent = await agentManager.spawnAgent('DEVTEAM_DOCS',
        'Create comprehensive documentation'
      );

      const documentation = await agentManager.runAgent(docsAgent,
        `Document: ${scope}`,
        { enableTools: true }
      );

      return {
        documentation,
        files: documentation.toolCalls.filter(t => t.tool.includes('write_file')).length
      };
    }
  },

  '/search': {
    name: 'Search Codebase',
    description: 'Find code, files, or patterns',
    agent: 'explore',
    workflow: async (agentManager, query) => {
      const exploreAgent = await agentManager.spawnAgent('EXPLORE',
        'Fast code search and file location'
      );

      const results = await agentManager.runAgent(exploreAgent,
        `Find: ${query}`,
        { enableTools: true }
      );

      return {
        results,
        matches: results.toolCalls.filter(t => t.tool.includes('search')).length
      };
    }
  },

  '/analyze': {
    name: 'Strategic Analysis',
    description: 'Deep analysis of complex problems',
    agent: 'ultrathink-strategist',
    workflow: async (agentManager, problem) => {
      const strategistAgent = await agentManager.spawnAgent('ULTRATHINK',
        'Deep multi-dimensional analysis'
      );

      const analysis = await agentManager.runAgent(strategistAgent,
        `Analyze: ${problem}`,
        { enableTools: false } // Thinking, not executing
      );

      return {
        analysis,
        insights: analysis.turns
      };
    }
  }
};

/**
 * Skill Executor
 */
export class SkillExecutor {
  constructor(agentManager) {
    this.agentManager = agentManager;
  }

  /**
   * Execute a skill by name
   */
  async executeSkill(skillName, ...args) {
    const skill = SKILLS[skillName];
    if (!skill) {
      throw new Error(`Unknown skill: ${skillName}`);
    }

    console.log(`[Skill] Executing ${skill.name}...`);

    const result = await skill.workflow(this.agentManager, ...args);

    console.log(`[Skill] ${skill.name} completed`);

    return {
      skill: skill.name,
      result,
      agent: skill.agent
    };
  }

  /**
   * List available skills
   */
  listSkills() {
    return Object.entries(SKILLS).map(([command, skill]) => ({
      command,
      name: skill.name,
      description: skill.description,
      agent: skill.agent
    }));
  }

  /**
   * Get skill help
   */
  getSkillHelp(skillName) {
    const skill = SKILLS[skillName];
    if (!skill) {
      return null;
    }

    return {
      command: skillName,
      name: skill.name,
      description: skill.description,
      agent: skill.agent,
      usage: `${skillName} <arguments>`
    };
  }
}

/**
 * Parse skill command from user input
 */
export function parseSkillCommand(input) {
  const trimmed = input.trim();

  if (!trimmed.startsWith('/')) {
    return null;
  }

  const parts = trimmed.split(/\s+/);
  const command = parts[0];
  const args = parts.slice(1).join(' ');

  if (SKILLS[command]) {
    return {
      command,
      args,
      skill: SKILLS[command]
    };
  }

  return null;
}
